import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { INCOME_DATA_YEAR, estimateIncomePercentile, getTopPercentLabel } from '@/lib/incomePercentile';

interface PercentileIndicatorProps {
  percentile: number;
  salary: number;
  className?: string;
}

const BADGE_VARIANTS: Record<string, 'default' | 'secondary' | 'outline'> = {
  'Top 1%': 'default',
  'Top 5%': 'secondary',
  'Top 10%': 'outline',
};

const getEliteBadge = (usPercentile: number) => {
  const label = getTopPercentLabel(usPercentile);
  if (!label || !(label in BADGE_VARIANTS)) return null;
  return { label, variant: BADGE_VARIANTS[label] };
};

const getTierInfo = (usPercentile: number) => {
  const eliteBadge = getEliteBadge(usPercentile);
  
  if (usPercentile >= 95) return { 
    label: "Elite Earner", 
    message: "🎉 Congratulations! You're in the top 5% of all US workers.", 
    color: "text-amber-600",
    badge: eliteBadge
  };
  if (usPercentile >= 75) return { 
    label: "High Earner", 
    message: "💪 Excellent! You're in the top 25% of all US workers.", 
    color: "text-emerald-600",
    badge: eliteBadge
  };
  if (usPercentile >= 50) return { 
    label: "Above Average", 
    message: "📊 Good position! You're earning above the US median income.", 
    color: "text-green-600",
    badge: null
  };
  if (usPercentile >= 25) return { 
    label: "Below Average", 
    message: "🚀 Room for growth. You're below the US median income.", 
    color: "text-yellow-600",
    badge: null
  };
  return { 
    label: "Entry Level", 
    message: "💡 Starting point. Focus on skill development for income growth.", 
    color: "text-orange-600",
    badge: null
  };
};

export function PercentileIndicator({ percentile, salary, className }: PercentileIndicatorProps) {
  const usPercentile = estimateIncomePercentile(salary);
  const tierInfo = getTierInfo(usPercentile);
  const position = Math.max(2, Math.min(98, usPercentile)); // Keep indicator within bounds

  return (
    <div className={cn("w-full space-y-4", className)}>
      {/* Elite Badge */}
      {tierInfo.badge && (
        <div className="flex justify-center">
          <Badge variant={tierInfo.badge.variant} className="text-sm font-semibold">
            {tierInfo.badge.label}
          </Badge>
        </div>
      )}

      {/* Tier Label and Message */}
      <div className="text-center space-y-2">
        <div className={cn("text-xl font-semibold", tierInfo.color)}>
          {tierInfo.label}
        </div>
        <div className="text-sm text-muted-foreground">
          {tierInfo.message}
        </div>
      </div>

      {/* Visual Gradient Bar */}
      <div className="relative">
        {/* Gradient Background */}
        <div className="h-8 w-full rounded-lg bg-gradient-to-r from-red-500 via-orange-500 via-yellow-500 via-lime-500 via-green-500 to-amber-500 shadow-sm">
          {/* Position Indicator */}
          <div 
            className="absolute top-0 h-8 w-1 bg-background border-2 border-foreground rounded-sm shadow-lg transform -translate-x-0.5 transition-all duration-500 ease-out"
            style={{ left: `${position}%` }}
          >
            {/* Arrow pointing down */}
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-foreground"></div>
          </div>
        </div>

        {/* Tier Labels Below Bar */}
        <div className="flex justify-between mt-3 text-xs sm:text-xs text-muted-foreground">
          <span className="text-[10px] sm:text-xs">Entry</span>
          <span className="text-[10px] sm:text-xs">Below Avg</span>
          <span className="text-[10px] sm:text-xs">Above Avg</span>
          <span className="text-[10px] sm:text-xs">High Earner</span>
          <span className="text-[10px] sm:text-xs">Elite</span>
        </div>
      </div>

      {/* Dual Percentile Stats */}
      <div className="space-y-2">
        <div className="text-center text-sm">
          <span className="font-medium text-primary">{Math.round(usPercentile)}%</span> of US workers earn less than{' '}
          <span className="font-medium">${salary.toLocaleString()}</span>
        </div>
        <div className="text-center text-xs text-muted-foreground">
          <span className="font-medium">{percentile}%</span> of job categories earn less (occupation comparison)
        </div>
      </div>

      {/* Context Note */}
      <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded border">
        <p>
          <strong>US Individual Income Comparison:</strong> Shows your earning position compared to all
          US individual earners based on {INCOME_DATA_YEAR} Census income data.{' '}
          <Link to="/income-percentile/" className="text-primary hover:underline underline-offset-2">
            See the full income percentile breakdown
          </Link>
          .
        </p>
      </div>
    </div>
  );
}